import { useModal } from '@shared/hooks/use-modal';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { DndFormValues, SuggestedStatusProps } from '../profile-dropdown.types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { dndSchema } from '../utils/schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetUserOnlineStatusQuery,
  useMarkAsActiveOrAwayMutation,
  UserActiveStatusEnum,
  useworkspaceAuthMeV2Query,
} from '@/types/generated/graphql';
import { getCurrentDateFromTimezone } from '@shared/utils/timezone.helper';
import { notification } from 'antd';
import { cacheOnly } from '@shared/constants';
import { defaultEmoji } from '../utils/dnd-constants';
import { formatFutureTime } from '../utils/format-future-time';
import { updateMyAwayStatus } from '../utils/user-away-status-handler';
import { useGetWorkspaceTimezone } from '@shared/hooks/use-get-workspace-timezone';

export function useDoNotDisturb() {
  const { t } = useTranslation();
  // Modal, suggestions and duration guggestions state management
  const { open, handleOpen, handleClose } = useModal();
  const [emoji, setEmoji] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [selectedDateTime, setSelectedDateTime] = useState<Dayjs | null>(null);

  // Form management with validation
  const { control, handleSubmit, reset, setValue, watch } = useForm<DndFormValues>({
    resolver: zodResolver(dndSchema(t)),
  });
  const statusValue = watch('status');

  //getting workspace timezone and current user workspace
  const { data: authMeWorkspaceData } = useworkspaceAuthMeV2Query(cacheOnly);
  const { timezone, transformDateFormat, convertToUTC } = useGetWorkspaceTimezone();
  const { currentUserWorkspace } = authMeWorkspaceData?.workspaceAuthMeV2?.data || {};

  //Managing user current online status
  const { data: userStatusData, loading } = useGetUserOnlineStatusQuery({
    variables: { userWorkspaceId: currentUserWorkspace?.id },
    skip: !currentUserWorkspace?.id,
  });

  const userActiveStatus = userStatusData?.getUserOnlineStatus?.data?.activeStatus === 'active';
  const dateTime = getCurrentDateFromTimezone(String(timezone));
  const timezoneDate = transformDateFormat(String(dateTime), 'YYYY-MM-DDTHH:mm:ss');
  const { message: awayStatusMessage, icon, awayTill } = userStatusData?.getUserOnlineStatus?.data || {};
  const awayTime = transformDateFormat(awayTill, 'YYYY-MM-DDTHH:mm:ss');
  const { time, label: timeLabel } = formatFutureTime(awayTime, String(timezoneDate)) || { time: '', label: '' };

  // Mutation to update user status
  const [updateUserStatus, { loading: updatingUserStatus }] = useMarkAsActiveOrAwayMutation({
    refetchQueries: ['GetUserOnlineStatus'],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      notification.success({ message: data.markAsActiveOrAway?.message });
      updateMyAwayStatus(currentUserWorkspace, userStatusData);
    },
    onError: (data) => {
      notification.error({ message: data?.message });
    },
  });

  //side effects to set form values when message or selected date time changes
  useEffect(() => {
    if (message) {
      setValue('status', message, { shouldValidate: true });
    }
    setValue('status', message);
  }, [message]);

  useEffect(() => {
    if (selectedDateTime) {
      setValue('date', selectedDateTime.format('YYYY-MM-DD'), { shouldValidate: true });
      setValue('time', selectedDateTime.format('HH:mm:ss'), { shouldValidate: true });
    }
  }, [selectedDateTime]);

  //setting user as active
  async function handleSetUserActive() {
    await updateUserStatus({
      variables: {
        input: {
          activeStatus: UserActiveStatusEnum?.active,
        },
      },
    });
  }
  //setting user as away on form submit
  async function onSubmit(data: DndFormValues) {
    const utcAwayTill = convertToUTC(data.date, data.time);
    await updateUserStatus({
      variables: {
        input: {
          activeStatus: UserActiveStatusEnum?.away,
          awayTill: utcAwayTill,
          message: message,
          icon: emoji || defaultEmoji,
        },
      },
    });
    handleClose();
    reset();
  }
  const handleDurationSelect = (seconds: number) => {
    const futureDate = dayjs(timezoneDate)?.add(seconds, 'second');
    setSelectedDateTime(futureDate);
    handleOpen();
  };
  const handleSuggestedStatus = (suggestion: SuggestedStatusProps) => {
    setEmoji(suggestion?.emoticon);
    setMessage(suggestion?.message);
    const futureDate = dayjs(timezoneDate)?.add(suggestion.duration, 'second');
    setSelectedDateTime(futureDate);
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDateTime(date);
    } else {
      setValue('date', '', { shouldValidate: false });
      setSelectedDateTime(null);
    }
  };

  const handleTimeChange = (time: Dayjs | null) => {
    if (time) {
      const updatedDateTime = selectedDateTime
        ? selectedDateTime.hour(time.hour()).minute(time.minute()).second(time.second())
        : time;
      setSelectedDateTime(updatedDateTime);
    } else {
      setValue('time', '', { shouldValidate: false });
      setSelectedDateTime(null);
    }
  };

  const handleModalClose = () => {
    setMessage('');
    setSelectedDateTime(null);
    setEmoji('');
    handleClose();
    reset();
  };
  return {
    open,
    control,
    handleSubmit,
    statusValue,
    onSubmit,
    handleDurationSelect,
    handleSuggestedStatus,
    handleDateChange,
    handleTimeChange,
    handleModalClose,
    setEmoji,
    handleOpen,
    selectedDateTime,
    setMessage,
    emoji,
    userActiveStatus,
    timezone,
    timezoneDate,
    updatingUserStatus,
    handleSetUserActive,
    userStatusData,
    awayStatusMessage,
    icon,
    time,
    timeLabel,
    awayTime,
    loading,
  };
}
